import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiErrors } from '@nx-customer-apps/shared/enums';
import { PRESERVE_NULL_KEYS } from '@nx-customer-apps/components';

@Injectable()
export class NoNullInterceptor implements HttpInterceptor {
    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        request = request.clone();

        if (['PATCH', 'PUT', 'POST'].includes(request.method) && request.url.includes('planning-projects')) {
            // Generic opt-out: a request may declare (via the PRESERVE_NULL_KEYS context token) specific keys
            // whose explicit `null` must be kept. This lets feature libraries (e.g. room-by-room) clear optional
            // backend fields by sending `null`, without this app-wide interceptor knowing anything about them.
            const preserveNullKeys = request.context.get(PRESERVE_NULL_KEYS);
            request = request.clone({ body: this.assertNoNulls(request.body, true, preserveNullKeys) });
        }

        return next.handle(request);
    }

    private assertNoNulls(payload: unknown, stripNulls = false, preserveNullKeys: string[] = []): unknown {
        return this.walkAndValidate(payload, 'payload', stripNulls, preserveNullKeys);
    }

    private walkAndValidate(value: unknown, path: string, stripNulls: boolean, preserveNullKeys: string[], key?: string): unknown {
        if (value === null || Number.isNaN(value)) {
            if (key !== undefined && preserveNullKeys.includes(key)) {
                return value;
            }

            console.warn(`PATCH payload contains null/NaN at ${path}, stripping it from payload`);
            if (stripNulls) {
                return undefined;
            }
            throw new HttpErrorResponse({
                status: 422,
                statusText: 'Unprocessable Entity',
                error: {
                    name: 'NoNullInterceptor',
                    status: 422,
                    key: ApiErrors.NullValue,
                    message: `PATCH payload contains null/NaN at ${path}`,
                    errors: []
                }
            });
        }

        if (Array.isArray(value)) {
            return value.map((item, index) => this.walkAndValidate(item, `${path}[${index}]`, stripNulls, preserveNullKeys));
        }

        if (this.isPlainObject(value)) {
            const result: Record<string, unknown> = { ...value };
            Object.keys(result).forEach(objectKey => {
                const nextValue = result[objectKey];
                result[objectKey] = this.walkAndValidate(nextValue, `${path}.${objectKey}`, stripNulls, preserveNullKeys, objectKey);
            });
            return result;
        }

        return value;
    }

    private isPlainObject(value: unknown): value is Record<string, unknown> {
        if (!value || typeof value !== 'object') {
            return false;
        }

        return Object.prototype.toString.call(value) === '[object Object]';
    }
}
